/**
 * TokenBalance Component (v2.0 - Platform Consistent)
 * Displays user's Q AI token balance with visual states
 * Uses platform green color instead of purple
 */

import React from 'react';
import { Chip, Typography, Tooltip } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';

/**
 * TokenBalance Component
 * @param {number} balance - Current token balance
 * @param {boolean} isLow - Whether balance is low (<=15)
 * @param {function} onBuyTokens - Callback when user wants to buy tokens
 * @param {string} size - 'small' | 'medium' (chip size)
 */
const TokenBalance = ({
  balance = 0,
  isLow = false,
  onBuyTokens,
  size = 'medium'
}) => {
  // Handle null/loading state
  const isLoading = balance === null;
  const displayBalance = isLoading ? '...' : balance;

  // Get background color for chip - platform consistent
  const getBgColor = () => {
    if (isLoading) return 'rgba(255, 255, 255, 0.05)';
    if (balance === 0) return 'rgba(237, 76, 92, 0.1)';
    if (isLow || balance <= 15) return 'rgba(245, 158, 11, 0.1)';
    return 'rgba(39, 181, 109, 0.1)';
  };

  // Get text color - platform consistent
  const getTextColor = () => {
    if (isLoading) return 'rgba(255, 255, 255, 0.5)';
    if (balance === 0) return 'rgba(237, 76, 92, 1)';
    if (isLow || balance <= 15) return '#F59E0B';
    return 'rgba(39, 181, 109, 1)';
  };

  // Get tooltip message
  const getTooltip = () => {
    if (isLoading) return 'Loading balance...';
    if (balance === 0) return 'No tokens remaining. Click to buy more.';
    if (isLow || balance <= 15) return 'Low balance. Click to buy more tokens.';
    return `${balance} tokens available`;
  };

  const isClickable = !isLoading && (isLow || balance <= 15 || balance === 0) && onBuyTokens;

  return (
    <Tooltip title={getTooltip()} arrow placement="bottom">
      <Chip
        icon={
          <BoltIcon
            sx={{
              color: `${getTextColor()} !important`,
              fontSize: size === 'small' ? 18 : 22 // Prominent icons
            }}
          />
        }
        label={
          <Typography
            sx={{
              fontFamily: "'JetBrains Mono', 'Roboto Mono', monospace", // Standardized font
              fontWeight: 600,
              // MOBILE FIX: 1rem (16px) minimum on all sizes for readability
              fontSize: size === 'small' ? '1rem' : '1.125rem',
              color: getTextColor(),
              whiteSpace: 'nowrap'
            }}
          >
            {displayBalance}
          </Typography>
        }
        onClick={isClickable ? onBuyTokens : undefined}
        sx={{
          backgroundColor: getBgColor(),
          border: `1px solid ${getTextColor()}40`,
          borderRadius: '8px', // Standardized to 8px
          height: size === 'small' ? 32 : 38, // Taller for better visibility
          flexShrink: 0,
          cursor: isClickable ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          '&:hover': isClickable
            ? {
                backgroundColor: getBgColor(),
                transform: 'translateY(-1px)',
                boxShadow: `0 4px 12px ${getTextColor()}30`
              }
            : {},
          '& .MuiChip-icon': {
            marginLeft: '6px',
            marginRight: '-4px'
          },
          '& .MuiChip-label': {
            paddingLeft: '6px',
            paddingRight: '10px'
          }
        }}
      />
    </Tooltip>
  );
};

export default TokenBalance;
